/**
 * A Case Prep workflow's prompt inside its user message: the workflow's icon
 * and one line, with the full instructions Casey received behind
 * "Show Instructions".
 */
import { ChevronRight } from "lucide-react";
import { useState, type FC } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

import type { WorkflowPromptMatch } from "../../workflows/catalog";

type Props = {
  match: WorkflowPromptMatch;
  /** The prompt as sent. */
  text: string;
};

export const WorkflowPromptBody: FC<Props> = ({ match, text }) => {
  const [open, setOpen] = useState(false);
  const Icon = match.def.icon;
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="flex min-w-0 flex-col gap-1"
      data-testid="workflow-prompt"
      data-workflow={match.def.id}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <Icon className="size-3.5 shrink-0 text-fg-3" aria-hidden />
        <span className="shrink-0 font-medium">{match.def.title}</span>
        {match.scope ? (
          <span
            className="min-w-0 truncate text-fg-3"
            data-testid="workflow-prompt-scope"
          >
            · {match.scope}
          </span>
        ) : null}
      </span>
      <CollapsibleTrigger
        className="inline-flex w-fit items-center gap-0.5 text-sm text-fg-3 hover:text-fg-2"
        data-testid="workflow-prompt-toggle"
      >
        <ChevronRight
          className={cn("size-3 transition-transform", open && "rotate-90")}
          aria-hidden
        />
        {open ? "Hide Instructions" : "Show Instructions"}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <p
          className="text-sm whitespace-pre-wrap text-fg-3"
          data-testid="workflow-prompt-text"
        >
          {text}
        </p>
      </CollapsibleContent>
    </Collapsible>
  );
};
